/**
 * Day formatting for the BULK orders action
 * (`POST /accounts/{uuid}/orders/actions`).
 *
 * The bulk endpoint takes `expectedDeliveryDate` as a BARE calendar
 * day (`yyyy-MM-dd`) and resolves it to midnight in each order's own
 * clinic time zone on the server. That is the opposite of the
 * single-order `PATCH /orders/{uuid}`, which wants a UTC instant and
 * is why `@shared/lib/zonedDay` exists.
 *
 * So this conversion is deliberately a STRING reshuffle with no
 * `Date`, no offsets and no zone lookups: running the day through a
 * zone here would shift it by one, which is exactly the bug the
 * single-order work shipped and then had to hotfix.
 */

/**
 * Convert the `DateField` value (`'MM/DD/YYYY'`) into the bare day the
 * bulk endpoint expects (`'YYYY-MM-DD'`).
 *
 * Returns `''` for anything that is not a complete, well-formed day,
 * so callers can treat a falsy result as "nothing to send".
 *
 * @param {string} formatted `'MM/DD/YYYY'` as produced by `DateField`
 * @returns {string} `'YYYY-MM-DD'`, or `''`
 */
export const toBulkDeliveryDay = formatted => {
  if (typeof formatted !== 'string') return '';
  const match = formatted.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return '';
  const [, month, day, year] = match;
  if (Number(month) < 1 || Number(month) > 12) return '';
  if (Number(day) < 1 || Number(day) > 31) return '';
  return `${year}-${month}-${day}`;
};

export default toBulkDeliveryDay;
